import React, { useState, useRef, useCallback } from 'react';
import {
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Backdrop,
    CircularProgress
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { rfidTagService } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications';
import { v4 as uuidv4 } from 'uuid';
import FormContainer, { FormContainerRef } from '@/components/form/FormContainer.tsx';
import EditorActions from '@/components/EditorActions.tsx';
import { RfidTagFilter } from './types.ts';
import FormTextField from '@/components/form/FormTextField.tsx';
import { required } from '@core/utils/validation.ts';
import { useSettingsStore } from '@core/store';

interface RfidTagFilterEditorProps {
    item: RfidTagFilter;
    onCancel: () => void;
    onChanged: () => void;
}

const RfidTagFilterEditor: React.FC<RfidTagFilterEditorProps> = ({ item, onCancel, onChanged }) => {
    const { t } = useTranslation();
    const { notifySuccess, notifyErrorResponse } = useNotifications();
    const [filter, setFilter] = useState(item.filter);
    const formRef = useRef<FormContainerRef>(null);
    const [formValid, setFormValid] = React.useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const { darkMode } = useSettingsStore();

    const handleSave = useCallback(async () => {
        if (!formValid) {
            return;
        }
        console.info('saving filter', filter);
        setIsSaving(true);

        const data = { ...item, filter } as RfidTagFilter;

        try {
            if (!item.id) {
                data.id = uuidv4();
                await rfidTagService.createRfidTagFilter(data);
            } else {
                await rfidTagService.updateRfidTagFilter(data);
            }

            notifySuccess();
            onChanged();
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setIsSaving(false);
        }
    }, [item, filter, onChanged]);

    const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(e.target.value);
    }, []);

    return (
        <Box>
            <Box className="editor-sidebar-actions">
                <EditorActions onSave={handleSave} onCancel={onCancel} saveDisabled={!formValid} />
            </Box>

            <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
                <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                    <Accordion
                        defaultExpanded
                        sx={{
                            boxShadow: 'none',
                            background: 'transparent',
                            '&::before': {
                                display: 'none'
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                padding: 0,
                                '& .MuiAccordionSummary-content': {
                                    margin: 0
                                }
                            }}
                        >
                            <Typography>
                                <i className="zmdi zmdi-view-list" style={{ marginRight: 8 }} />
                                {t('general')}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <FormTextField
                                name={'filter'}
                                fullWidth
                                label={t('filter')}
                                value={filter}
                                onChange={handleFilterChange}
                                required
                                rules={[required(t)]}
                            />
                        </AccordionDetails>
                    </Accordion>
                </FormContainer>
                {isSaving && (
                    <Backdrop
                        sx={{
                            position: 'absolute',
                            color: '#464D69',
                            zIndex: 1000,
                            bgcolor: darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'
                        }}
                        open={isSaving}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                )}
            </Box>
        </Box>
    );
};

export default RfidTagFilterEditor;
